import { Head, Link, useForm } from "@inertiajs/react"
import { useRef, useState } from "react"
import AdminLayout from "@/components/admin/layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export default function AdminAccountsNew() {
  const { data, setData, post, processing, errors } = useForm<{
    user_name: string
    user_email: string
    user_password: string
    account_name: string
    account_type: string
    brand_colour: string
    website_url: string
    agency_name: string
    agency_address: string
    agency_phone: string
    logo: File | null
  }>({
    user_name: "",
    user_email: "",
    user_password: "",
    account_name: "",
    account_type: "individual",
    brand_colour: "#C41E2A",
    website_url: "",
    agency_name: "",
    agency_address: "",
    agency_phone: "",
    logo: null,
  })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)

  function generatePassword() {
    const chars = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789"
    let password = ""
    for (let i = 0; i < 16; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length))
    }
    setData("user_password", password)
  }

  function handleLogoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setData("logo", file)
    setLogoPreview(URL.createObjectURL(file))
  }

  function removeLogo() {
    setData("logo", null)
    setLogoPreview(null)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    post("/admin/accounts", { forceFormData: true })
  }

  return (
    <AdminLayout>
      <Head title="New Account | Admin" />
      <div className="mx-auto max-w-2xl space-y-6">
        <div className="flex items-center gap-4">
          <Link href="/admin/accounts" className="text-cream/40 hover:text-cream transition-colors">
            <ArrowLeftIcon className="size-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-display font-semibold text-cream">New account</h1>
            <p className="mt-1 text-sm text-cream/50">Create a new client account with an owner user.</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* User section */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5 space-y-4">
            <h2 className="text-[11px] font-medium uppercase tracking-wider text-cream/40">Owner user</h2>

            <div className="space-y-1.5">
              <Label className="text-cream/70">Name</Label>
              <Input
                value={data.user_name}
                onChange={(e) => setData("user_name", e.target.value)}
                placeholder="e.g. John Smith"
                className="border-white/10 bg-white/5 text-cream"
              />
              {errors.user_name && <p className="text-xs text-red">{errors.user_name}</p>}
            </div>

            <div className="space-y-1.5">
              <Label className="text-cream/70">Email</Label>
              <Input
                type="email"
                value={data.user_email}
                onChange={(e) => setData("user_email", e.target.value)}
                placeholder="e.g. john@agency.co.uk"
                className="border-white/10 bg-white/5 text-cream"
              />
              {errors.user_email && <p className="text-xs text-red">{errors.user_email}</p>}
            </div>

            <div className="space-y-1.5">
              <Label className="text-cream/70">Password</Label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={data.user_password}
                  onChange={(e) => setData("user_password", e.target.value)}
                  placeholder="Minimum 6 characters"
                  className="border-white/10 bg-white/5 text-cream font-mono text-sm"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="shrink-0 border-white/10 text-cream/70 hover:text-cream"
                  onClick={generatePassword}
                >
                  Generate
                </Button>
              </div>
              {errors.user_password && <p className="text-xs text-red">{errors.user_password}</p>}
            </div>
          </div>

          {/* Account section */}
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5 space-y-4">
            <h2 className="text-[11px] font-medium uppercase tracking-wider text-cream/40">Account</h2>

            <div className="space-y-1.5">
              <Label className="text-cream/70">Account name</Label>
              <Input
                value={data.account_name}
                onChange={(e) => setData("account_name", e.target.value)}
                placeholder="e.g. Smith & Partners"
                className="border-white/10 bg-white/5 text-cream"
              />
              {errors.account_name && <p className="text-xs text-red">{errors.account_name}</p>}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-cream/70">Account type</Label>
                <Select value={data.account_type} onValueChange={(v) => setData("account_type", v)}>
                  <SelectTrigger className="border-white/10 bg-white/5 text-cream">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="individual">Individual</SelectItem>
                    <SelectItem value="agency">Agency</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label className="text-cream/70">Brand colour</Label>
                <div className="flex gap-2">
                  <Input
                    value={data.brand_colour}
                    onChange={(e) => setData("brand_colour", e.target.value)}
                    placeholder="#C41E2A"
                    className="border-white/10 bg-white/5 text-cream font-mono text-sm"
                  />
                  <input
                    type="color"
                    value={data.brand_colour}
                    onChange={(e) => setData("brand_colour", e.target.value)}
                    className="h-9 w-9 shrink-0 cursor-pointer rounded-md border border-white/10 bg-white/5 p-0.5"
                  />
                </div>
                {errors.brand_colour && <p className="text-xs text-red">{errors.brand_colour}</p>}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-cream/70">Website URL</Label>
              <Input
                type="url"
                value={data.website_url}
                onChange={(e) => setData("website_url", e.target.value)}
                placeholder="e.g. https://smithandpartners.co.uk"
                className="border-white/10 bg-white/5 text-cream"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-cream/70">Logo</Label>
              <div className="flex items-start gap-4">
                <div className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border border-white/10 bg-white/5">
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo preview" className="size-full object-contain p-1" />
                  ) : (
                    <ImageIcon className="size-6 text-cream/20" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="border-white/10 text-cream/70 hover:text-cream"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {logoPreview ? "Change" : "Upload logo"}
                    </Button>
                    {logoPreview && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="text-cream/40 hover:text-red"
                        onClick={removeLogo}
                      >
                        Remove
                      </Button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={handleLogoChange}
                      className="hidden"
                    />
                  </div>
                  <p className="mt-1.5 text-[11px] text-cream/30">PNG, JPEG, or WebP. Max 2 MB.</p>
                </div>
              </div>
              {errors.logo && <p className="text-xs text-red">{errors.logo}</p>}
            </div>
          </div>

          {/* Agency section (conditional) */}
          {data.account_type === "agency" && (
            <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-5 space-y-4">
              <h2 className="text-[11px] font-medium uppercase tracking-wider text-cream/40">Agency details</h2>

              <div className="space-y-1.5">
                <Label className="text-cream/70">Agency name</Label>
                <Input
                  value={data.agency_name}
                  onChange={(e) => setData("agency_name", e.target.value)}
                  placeholder="e.g. Smith & Partners Estate Agents"
                  className="border-white/10 bg-white/5 text-cream"
                />
                {errors.agency_name && <p className="text-xs text-red">{errors.agency_name}</p>}
              </div>

              <div className="space-y-1.5">
                <Label className="text-cream/70">Agency address</Label>
                <Input
                  value={data.agency_address}
                  onChange={(e) => setData("agency_address", e.target.value)}
                  placeholder="e.g. 12 High Street, Bristol, BS1 2AB"
                  className="border-white/10 bg-white/5 text-cream"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="text-cream/70">Agency phone</Label>
                <Input
                  type="tel"
                  value={data.agency_phone}
                  onChange={(e) => setData("agency_phone", e.target.value)}
                  placeholder="e.g. 0117 123 4567"
                  className="border-white/10 bg-white/5 text-cream"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              className="text-cream/50 hover:text-cream"
              onClick={() => window.history.back()}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>
              {processing ? "Creating..." : "Create account"}
            </Button>
          </div>
        </form>
      </div>
    </AdminLayout>
  )
}

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
    </svg>
  )
}

function ImageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
    </svg>
  )
}
